export interface PayTarget {
  /** Where "Pay now" goes: the branded /pay/<slug> link when the booking has one, else the processor's own page. */
  url: string;
  /** What that link charges, to the cent: the amount due. */
  amount: number;
}

const LIVE = new Set(["pending", "booked", "updated"]);
const DONE = new Set(["complete", "completed", "paid", "expired", "canceled", "cancelled", "failed", "void"]);

/**
 * The payment link an unpaid pay-later booking already has (1NOW-11156), found
 * in the booking the renter's own session reads (GET /api/bookings/<uuid>/):
 * its Stripe and Square checkout sessions, each carrying `url` and, once the
 * backend has made one, the branded `short_url` (/pay/<slug>).
 *
 * Only a link that can settle what is owed: the booking live, the session not
 * finished or expired, not a security-deposit link, on https. Anything else is
 * null and the page says the link is still being made, never a dead button.
 * The newest wins; the backend reuses an open link for the same amount.
 */
export const payTargetFor = (booking: any, now: Date = new Date()): PayTarget | null => {
  if (!booking || !LIVE.has(String(booking.trip_status ?? ""))) return null;
  if (booking.cancellation_pending === true) return null;
  const sessions: any[] = [
    ...(Array.isArray(booking.checkout_sessions) ? booking.checkout_sessions : []),
    ...(Array.isArray(booking.square_checkout_sessions) ? booking.square_checkout_sessions : []),
  ].sort((a, b) => (Date.parse(b?.created) || 0) - (Date.parse(a?.created) || 0));
  for (const s of sessions) {
    if (s?.is_security_deposit === true || s?.metadata?.link_purpose === "security_deposit") continue;
    if (DONE.has(String(s?.status ?? "").toLowerCase())) continue;
    if (s?.expires_at && !(Date.parse(s.expires_at) > now.getTime())) continue;
    const amount = Number(s?.amount);
    if (!Number.isFinite(amount) || amount <= 0) continue;
    for (const raw of [s?.short_url, s?.url]) {
      try {
        const u = new URL(String(raw));
        if (u.protocol === "https:") return { url: u.toString(), amount };
      } catch {
        /* not a link */
      }
    }
  }
  return null;
};
